/**
 * 简易 Toast 总线：subscribe/publish 模式，无外部依赖。
 *
 * 去重策略：相同 (message, tone) 在 `DEDUP_WINDOW_MS` 窗口内只发一次。
 * 这覆盖两种场景：
 *   - React StrictMode dev 期双调 useEffect 导致同一个失败 toast 出现两次；
 *   - 路由快速切换 + effect 重入让同一个错误被反复上报。
 * 去重窗口以"已有最近一次展示时间"为准，所以连续重复调用会推迟
 * 可见 toast 的 TTL，但绝不会堆积成屏。
 */

import React, { useEffect, useState } from 'react';

type ToastTone = 'info' | 'success' | 'warning' | 'danger';

interface ToastItem {
  id: number;
  message: string;
  tone: ToastTone;
}

const DEDUP_WINDOW_MS = 1200;

let toastId = 0;
const listeners = new Set<(items: ToastItem[]) => void>();
let toasts: ToastItem[] = [];
/** 最近一次发出的 (message|tone) → unix ms，用于去重窗口判定。 */
const recentEmit = new Map<string, number>();

function emit() {
  for (const l of listeners) l([...toasts]);
}

function dedupKey(message: string, tone: ToastTone): string {
  return `${tone}|${message}`;
}

export function showToast(message: string, tone: ToastTone = 'info', ttl = 2400) {
  const key = dedupKey(message, tone);
  const now = Date.now();
  const last = recentEmit.get(key);
  if (last !== undefined && now - last < DEDUP_WINDOW_MS) {
    // 在去重窗口内已经有相同的 toast，跳过本次 emit。
    // 不重置 TTL——避免把刚刚快要消失的 toast 又延后到完整 2.4s，
    // 让短促的错误感觉更短而不是被拖得更长。
    return;
  }
  recentEmit.set(key, now);

  const id = ++toastId;
  toasts = [...toasts, { id, message, tone }];
  emit();
  setTimeout(() => {
    toasts = toasts.filter((t) => t.id !== id);
    emit();
    // 早于去重窗口结束时不清理 recentEmit，让同 key 的重复调用继续命中去重。
  }, ttl);
}

const toneStyle: Record<ToastTone, React.CSSProperties> = {
  // 信息与成功提示属于产品正向反馈，跟随当前主题主色；
  // warning / danger 保留跨主题稳定的语义色，避免用户误判风险等级。
  info: { background: 'var(--fp-primary)' },
  success: { background: 'var(--fp-primary)' },
  warning: { background: 'var(--fp-warning)' },
  danger: { background: 'var(--fp-danger)' },
};

export const ToastHost: React.FC = () => {
  const [items, setItems] = useState<ToastItem[]>([]);
  useEffect(() => {
    listeners.add(setItems);
    return () => {
      listeners.delete(setItems);
    };
  }, []);
  return (
    <div
      aria-live="polite"
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        paddingTop: 'max(72px, env(safe-area-inset-top, 0px))',
        zIndex: 9999,
      }}
    >
      {items.map((item) => (
        <div
          key={item.id}
          data-testid={`toast-${item.tone}`}
          style={{
            ...toneStyle[item.tone],
            color: '#fff',
            padding: '10px 16px',
            borderRadius: 999,
            fontSize: 13,
            fontWeight: 600,
            boxShadow: '0 8px 28px rgba(0,0,0,0.18)',
            maxWidth: '80%',
            textAlign: 'center',
          }}
        >
          {item.message}
        </div>
      ))}
    </div>
  );
};
